import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AppStateProvider } from './context/AppStateContext';
import { ForecastProvider } from './context/ForecastContext';
import { LoginPage } from './pages/LoginPage';
import { AppPage } from './pages/AppPage';
import { ChargeMonthlyPage } from './pages/ChargeMonthlyPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { Suspense, lazy } from 'react';
import { Skeleton } from './components/ui/skeleton';
import { Toaster } from 'sonner';

const DashboardSection = lazy(() => import('./components/sections/dashboard/DashboardSection').then(m => ({ default: m.DashboardSection })));
const SyntheseSection = lazy(() => import('./components/sections/synthese/SyntheseSection').then(m => ({ default: m.SyntheseSection })));
const RevenusSection = lazy(() => import('./components/sections/revenus/RevenusSection').then(m => ({ default: m.RevenusSection })));
const ChargesSection = lazy(() => import('./components/sections/charges/ChargesSection').then(m => ({ default: m.ChargesSection })));
const SalairesSection = lazy(() => import('./components/sections/salaires/SalairesSection').then(m => ({ default: m.SalairesSection })));
const DirigeantSection = lazy(() => import('./components/sections/dirigeant/DirigeantSection').then(m => ({ default: m.DirigeantSection })));
const FiscaliteSection = lazy(() => import('./components/sections/fiscalite/FiscaliteSection').then(m => ({ default: m.FiscaliteSection })));
const RapportsSection = lazy(() => import('./components/sections/rapports/RapportsSection').then(m => ({ default: m.RapportsSection })));
const ScenariosSection = lazy(() => import('./components/sections/scenarios/ScenariosSection').then(m => ({ default: m.ScenariosSection })));
const InvestissementsSection = lazy(() => import('./components/sections/investissements/InvestissementsSection').then(m => ({ default: m.InvestissementsSection })));
const NotairesSection = lazy(() => import('./components/sections/notaires/NotairesSection').then(m => ({ default: m.NotairesSection })));
const UsersSection = lazy(() => import('./components/sections/users/UsersSection').then(m => ({ default: m.UsersSection })));
const ProfileSection = lazy(() => import('./components/sections/profile/ProfileSection').then(m => ({ default: m.ProfileSection })));

function SectionFallback() {
  return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
      </div>
      <Skeleton className="h-64 rounded-lg" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        richColors
        expand
        toastOptions={{ style: { fontWeight: 500, fontSize: '14px' } }}
      />
      <AuthProvider>
        <AppStateProvider>
          <ForecastProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/" element={<AppPage />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Suspense fallback={<SectionFallback />}><DashboardSection /></Suspense>} />
                <Route path="synthese" element={<Suspense fallback={<SectionFallback />}><SyntheseSection /></Suspense>} />
                <Route path="revenus" element={<Suspense fallback={<SectionFallback />}><RevenusSection /></Suspense>} />
                <Route path="charges" element={<Suspense fallback={<SectionFallback />}><ChargesSection /></Suspense>} />
                <Route path="charges/:catKey/:chargeId/monthly" element={<ChargeMonthlyPage />} />
                <Route path="investissements" element={<Suspense fallback={<SectionFallback />}><InvestissementsSection /></Suspense>} />
                <Route path="salaires" element={<Suspense fallback={<SectionFallback />}><SalairesSection /></Suspense>} />
                <Route path="notaires" element={<Suspense fallback={<SectionFallback />}><NotairesSection /></Suspense>} />
                <Route path="dirigeant" element={<Suspense fallback={<SectionFallback />}><DirigeantSection /></Suspense>} />
                <Route path="fiscalite" element={<Suspense fallback={<SectionFallback />}><FiscaliteSection /></Suspense>} />
                <Route path="scenarios" element={<Suspense fallback={<SectionFallback />}><ScenariosSection /></Suspense>} />
                <Route path="users" element={<Suspense fallback={<SectionFallback />}><UsersSection /></Suspense>} />
                <Route path="profile" element={<Suspense fallback={<SectionFallback />}><ProfileSection /></Suspense>} />
                <Route path="rapports" element={<Suspense fallback={<SectionFallback />}><RapportsSection /></Suspense>} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ForecastProvider>
        </AppStateProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
